import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaReceipt, FaFileInvoice, FaTimes, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import api from '../../../utils/api';
import useColleges from '../../../utils/useColleges';
import { useAuth } from '../../../context/AuthContext';
import './ReceiptLog.css';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ReceiptLog = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { colleges } = useColleges();

  const isScoped = user?.role === 'collegeadmin' || user?.role === 'clerk';
  const basePath = user?.role === 'collegeadmin' ? '/collegeadmin'
    : user?.role === 'clerk' ? '/clerk' : '/admin';

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  // Date range filter - default to current month
  const getCurrentMonthRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    // First day of current month
    const firstDay = new Date(year, month, 1);
    // Last day of current month
    const lastDay = new Date(year, month + 1, 0);
    
    // Format as YYYY-MM-DD
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    return {
      from: formatDate(firstDay),
      to: formatDate(lastDay)
    };
  };

  const [dateFrom, setDateFrom] = useState(getCurrentMonthRange().from);
  const [dateTo, setDateTo] = useState(getCurrentMonthRange().to);

  // filters
  const [search,         setSearch]         = useState('');
  const [filterCollege,  setFilterCollege]  = useState('');
  const [filterSession,  setFilterSession]  = useState('');
  const [filterClass,    setFilterClass]    = useState('');
  const [filterBatch,    setFilterBatch]    = useState('');
  const [filterMedium,   setFilterMedium]   = useState('');
  const [filterSort,     setFilterSort]     = useState('newest');
  const [filterReceiptType, setFilterReceiptType] = useState(''); // 'receipt' | 'serial' | ''

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/fees/allocations');
        const allocs = data?.data || [];

        const flat = [];
        allocs.forEach((alloc) => {
          (alloc.transactions || []).forEach((txn) => {
            if (!txn.receiptNo || txn.receiptNo.startsWith('PEND')) return;
            const isSerial = /^\d+$/.test(txn.receiptNo);
            flat.push({
              receiptNo:    txn.receiptNo,
              receiptType:  isSerial ? 'serial' : 'receipt',
              studentName:  alloc.student?.name       || txn.studentName  || '—',
              fatherName:   alloc.student?.fatherName || '—',
              classCourse:  txn.studentClass || alloc.class   || '—',
              batch:        txn.studentBatch || alloc.batch   || '—',
              feeSession:   txn.sessionName  || alloc.session || '—',
              medium:       alloc.student?.class?.medium || alloc.student?.medium || '',
              collegeName:  alloc.college?.name || '—',
              collegeId:    String(alloc.college?._id || alloc.college || ''),
              totalPaid:    Number(txn.paidAmount || 0),
              paidAt:       txn.paidAt,
              _alloc: alloc,
              _txn:   txn,
            });
          });
        });

        setRows(flat);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // derive filter options from rows
  const sessionOptions = useMemo(() => [...new Set(rows.map(r => r.feeSession).filter(Boolean))].sort(), [rows]);
  const classOptions   = useMemo(() => [...new Set(rows.map(r => r.classCourse).filter(Boolean))].sort(), [rows]);
  const batchOptions   = useMemo(() => [...new Set(rows.map(r => r.batch).filter(Boolean))].sort(), [rows]);
  const mediumOptions  = useMemo(() => [...new Set(rows.map(r => r.medium).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = rows.filter((r) => {
      const matchSearch  = !q || [r.receiptNo, r.studentName, r.fatherName, r.classCourse, r.batch, r.feeSession]
        .some(v => v.toLowerCase().includes(q));
      const matchCollege = !filterCollege  || r.collegeId === filterCollege;
      const matchSession = !filterSession  || r.feeSession   === filterSession;
      const matchClass   = !filterClass    || r.classCourse  === filterClass;
      const matchBatch   = !filterBatch    || r.batch        === filterBatch;
      const matchMedium  = !filterMedium   || r.medium       === filterMedium;
      const matchType    = !filterReceiptType || r.receiptType === filterReceiptType;
      
      // Date range filter
      let matchDate = true;
      if (dateFrom || dateTo) {
        if (!r.paidAt) {
          matchDate = false;
        } else {
          const paidDate = new Date(r.paidAt);
          const fromDate = dateFrom ? new Date(dateFrom) : null;
          const toDate = dateTo ? new Date(dateTo) : null;
          
          if (fromDate) fromDate.setHours(0, 0, 0, 0);
          if (toDate) toDate.setHours(23, 59, 59, 999);
          paidDate.setHours(0, 0, 0, 0);
          
          if (fromDate && toDate) {
            matchDate = paidDate >= fromDate && paidDate <= toDate;
          } else if (fromDate) {
            matchDate = paidDate >= fromDate;
          } else if (toDate) {
            matchDate = paidDate <= toDate;
          }
        }
      }
      
      return matchSearch && matchCollege && matchSession && matchClass && matchBatch && matchMedium && matchType && matchDate;
    });

    // Sort by receipt number
    const extractSeq = (no) => {
      const parts = String(no).split('/');
      return parseInt(parts[parts.length - 1], 10) || 0;
    };
    list = [...list].sort((a, b) =>
      filterSort === 'newest'
        ? extractSeq(b.receiptNo) - extractSeq(a.receiptNo)
        : extractSeq(a.receiptNo) - extractSeq(b.receiptNo)
    );

    // Add sequential serial numbers AFTER sorting
    return list.map((r, index) => ({ ...r, displaySerial: index + 1 }));
  }, [rows, search, filterCollege, filterSession, filterClass, filterBatch, filterMedium, filterSort, filterReceiptType, dateFrom, dateTo]);

  // group by college and add college-specific serial numbers
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(r => {
      const key = r.collegeName || 'Unknown College';
      if (!g[key]) g[key] = [];
      g[key].push(r);
    });
    
    // Add serial numbers for each college separately
    Object.keys(g).forEach(collegeName => {
      g[collegeName] = g[collegeName].map((r, index) => ({
        ...r,
        collegeSerial: index + 1
      }));
    });
    
    return g;
  }, [filtered]);

  const clearFilters = () => {
    setSearch(''); setFilterCollege(''); setFilterSession('');
    setFilterClass(''); setFilterBatch(''); setFilterMedium(''); setFilterSort('newest'); setFilterReceiptType('');
    const range = getCurrentMonthRange();
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  const hasFilters = search || filterCollege || filterSession || filterClass || filterBatch || filterMedium || filterReceiptType || filterSort !== 'newest';

  const openReceipt = (row) => {
    const alloc = row._alloc;
    const txn   = row._txn;
    const items = (txn.items || []).map(i => ({ type: i.type, amountPaid: Number(i.amountPaid) || 0 }));
    const modes = txn.paymentModes?.length
      ? txn.paymentModes
      : [{ mode: 'Cash', referenceNo: '—', amount: txn.paidAmount }];

    // Use full college object from colleges array (has address, logo, titleHeader, etc.)
    const fullCollege = colleges.find(c => String(c._id) === String(alloc.college?._id || alloc.college)) || alloc.college || {};

    navigate(`${basePath}/fee-receipt`, {
      state: {
        allocation: { college: fullCollege, student: alloc.student || {} },
        payment: {
          receiptNo:               txn.receiptNo,
          paidAt:                  txn.paidAt,
          paidAmount:              Number(txn.paidAmount || 0),
          paymentModes:            modes,
          items,
          balanceAfterTransaction: txn.balanceAfterTransaction ?? 0,
          feeName:                 txn.feeName    || alloc.feeName  || 'Fee',
          studentClass:            txn.studentClass || alloc.class  || 'N/A',
          sessionName:             txn.sessionName  || alloc.session || 'N/A',
          itemsAtTransaction:      txn.itemsAtTransaction || items,
        },
      },
    });
  };

  return (
    <div className="rl-container">

      {/* Header */}
      <h2 className="rl-title"><FaReceipt /> Receipt Log</h2>

      {/* Filters */}
      <div className="rl-controls">
        <div className="rl-search-wrapper">
          <FaSearch className="rl-search-icon" />
          <input
            className="rl-search-input"
            placeholder="Search receipt no, student, class…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="rl-clear-btn" onClick={() => setSearch('')}><FaTimes /></button>}
        </div>

        {!isScoped && colleges.length > 1 && (
          <select className="rl-filter-select" value={filterCollege} onChange={e => setFilterCollege(e.target.value)}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <select className="rl-filter-select" value={filterSession} onChange={e => setFilterSession(e.target.value)}>
          <option value="">All Sessions</option>
          {sessionOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <select className="rl-filter-select" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
          <option value="">All Classes</option>
          {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select className="rl-filter-select" value={filterBatch} onChange={e => setFilterBatch(e.target.value)}>
          <option value="">All Batches</option>
          {batchOptions.map(b => <option key={b} value={b}>{b}</option>)}
        </select>

        <select className="rl-filter-select" value={filterMedium} onChange={e => setFilterMedium(e.target.value)}>
          <option value="">All Mediums</option>
          {mediumOptions.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        <select className="rl-filter-select" value={filterReceiptType} onChange={e => setFilterReceiptType(e.target.value)}>
          <option value="">Receipt No &amp; Serial</option>
          <option value="receipt">Receipt No Only</option>
          <option value="serial">Serial No Only</option>
        </select>

        <select className="rl-filter-select" value={filterSort} onChange={e => setFilterSort(e.target.value)}>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>

        {hasFilters && (
          <button className="rl-clear-all-btn" onClick={clearFilters}>
            <FaTimes /> Clear
          </button>
        )}
      </div>

      {/* Date Range Filter */}
      <div style={{ 
        background: '#f8fafc', 
        border: '1px solid #e2e8f0', 
        borderRadius: '8px', 
        padding: '16px', 
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontWeight: 600 }}>
          <FaCalendarAlt style={{ color: '#3b82f6' }} />
          Date Range:
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>From:</label>
          <input 
            type="date" 
            value={dateFrom} 
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '0.9rem',
              outline: 'none',
              cursor: 'pointer'
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>To:</label>
          <input 
            type="date" 
            value={dateTo} 
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '0.9rem',
              outline: 'none',
              cursor: 'pointer'
            }}
          />
        </div>
        <button
          onClick={() => {
            const range = getCurrentMonthRange();
            setDateFrom(range.from);
            setDateTo(range.to);
          }}
          style={{
            padding: '6px 12px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <FaCalendarAlt /> Current Month
        </button>
        <button
          onClick={() => {
            setDateFrom('');
            setDateTo('');
          }}
          style={{
            padding: '6px 12px',
            background: '#64748b',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <FaTimes /> Show All
        </button>
      </div>

      {/* Summary bar */}
      <div className="rl-summary-bar">
        <span className="rl-count-badge">
          <FaFilter /> {filtered.length} receipt{filtered.length !== 1 ? 's' : ''}
        </span>
        <span className="rl-total-badge">
          Total Collected: ₹ {filtered.reduce((s, r) => s + r.totalPaid, 0).toLocaleString()}
        </span>
      </div>

      {/* Overall Summary Card */}
      {filtered.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          color: 'white',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '4px' }}>Overall Summary</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>₹ {filtered.reduce((s, r) => s + r.totalPaid, 0).toLocaleString()}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '4px' }}>Total Receipts</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>{filtered.length}</div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="rl-state-box">Loading receipts…</div>
      ) : filtered.length === 0 ? (
        <div className="rl-state-box">No receipts found.</div>
      ) : (
        <div className="rl-grouped-container">
          {Object.entries(grouped).map(([collegeName, list]) => {
            const collegeTotal = list.reduce((s, r) => s + r.totalPaid, 0);
            const collegeCount = list.length;
            
            return (
              <div key={collegeName} className="rl-table-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                  <h3 className="rl-group-header" style={{ margin: 0 }}>🏫 {collegeName}</h3>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ background: '#f0f9ff', padding: '8px 16px', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <span style={{ fontSize: '0.8rem', color: '#0369a1', fontWeight: 600 }}>Receipts: </span>
                      <span style={{ fontSize: '1rem', color: '#0c4a6e', fontWeight: 700 }}>{collegeCount}</span>
                    </div>
                    <div style={{ background: '#f0fdf4', padding: '8px 16px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <span style={{ fontSize: '0.8rem', color: '#15803d', fontWeight: 600 }}>Total: </span>
                      <span style={{ fontSize: '1rem', color: '#14532d', fontWeight: 700 }}>₹ {collegeTotal.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              <div className="rl-table-wrapper">
                <table className="rl-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Receipt / Serial No</th>
                      <th className="rl-left">Student Name</th>
                      <th>Father Name</th>
                      <th>Class / Course</th>
                      <th>Batch</th>
                      <th>Fee Session</th>
                      <th>Total Paid</th>
                      <th>Date</th>
                      <th>Payment Mode</th>
                      <th>Reference No</th>
                      <th>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((row) => {
                      const txn = row._txn;
                      const modes = txn?.paymentModes?.length > 0 
                        ? txn.paymentModes 
                        : [{ mode: 'Cash', referenceNo: '—' }];
                      const primaryMode = modes[0] || { mode: 'Cash', referenceNo: '—' };
                      
                      return (
                        <tr key={row.receiptNo + row.collegeSerial} className="rl-row">
                          <td className="rl-serial">{row.collegeSerial}</td>
                          <td>
                            <span className={`rl-receipt-no ${row.receiptType === 'serial' ? 'rl-serial-type' : ''}`}>
                              {row.receiptNo}
                            </span>
                          </td>
                          <td className="rl-left rl-bold">{row.studentName}</td>
                          <td>{row.fatherName}</td>
                          <td>
                            <span className="rl-class-badge">{row.classCourse}</span>
                            {row.medium && <div style={{fontSize:'0.72rem',color:'#7c3aed',marginTop:'2px'}}>{row.medium}</div>}
                          </td>
                          <td><span className="rl-batch-badge">{row.batch}</span></td>
                          <td><span className="rl-session-badge">{row.feeSession}</span></td>
                          <td className="rl-amount">₹ {row.totalPaid.toLocaleString()}</td>
                          <td className="rl-date">{fmtDate(row.paidAt)}</td>
                          <td>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              background: primaryMode.mode === 'Cash' ? '#fef3c7' : '#dbeafe',
                              color: primaryMode.mode === 'Cash' ? '#92400e' : '#1e40af'
                            }}>
                              {primaryMode.mode || 'Cash'}
                            </span>
                            {modes.length > 1 && (
                              <div style={{fontSize:'0.72rem',color:'#64748b',marginTop:'2px'}}>+{modes.length - 1} more</div>
                            )}
                          </td>
                          <td>{primaryMode.referenceNo || '—'}</td>
                          <td>
                            <button className="rl-view-btn" onClick={() => openReceipt(row)}>
                              <FaFileInvoice /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="rl-foot-row">
                      <td colSpan="7" className="rl-foot-label">College Total ({list.length} receipts)</td>
                      <td className="rl-foot-amount">
                        ₹ {list.reduce((s, r) => s + r.totalPaid, 0).toLocaleString()}
                      </td>
                      <td colSpan="4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
};

export default ReceiptLog;
